import type { CueName } from "../engine/RunSession";

type Cue = CueName | "ui" | "high" | "countdown";

type AudioContextClass = typeof AudioContext;

/** The page's AudioContext class (old iOS Safari only has the prefixed one); undefined when there is no WebAudio. */
function contextClass(): AudioContextClass | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window as unknown as { AudioContext?: AudioContextClass; webkitAudioContext?: AudioContextClass };
  return w.AudioContext ?? w.webkitAudioContext;
}

/** Events browsers accept as a user gesture for audio (touchstart and a touch pointerdown do not count). */
const GESTURES = ["pointerup", "touchend", "mousedown", "click", "keydown"] as const;
/** 0.25 s of silence: looped by an <audio> element from the first tap on older iPhones (see unmuteIOS) */
const SILENT_WAV = "data:audio/wav;base64,UklGRvQHAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YdAHAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==";

/**
 * The soundtrack (public/sounds/music/, all CC0 — see public/sounds/SOURCES.md). Runs play a shuffled playlist:
 * every run starts on a different track, a track that ends crossfades into the next, never the same one twice in a
 * row. The menu loops one calmer track. Files that are missing are skipped; the synth pulse plays only when none exist.
 */
export const RUN_TRACKS = ["music/hot-roadway", "music/sky-blue-street", "music/cyber-runner", "music/awake", "music/the-rush", "music/slampe"];
export const MENU_TRACK = "music/chill-lofi";
/** what the `siren` cue plays: the chaser honking (public/sounds/horn.mp3) */
const CHASE_SOUND = "horn";
/** seconds the outgoing and incoming tracks overlap */
const CROSSFADE = 1.5;
/** the next track is fetched and decoded this many seconds before the current one ends (decoded music is ~0.4 MB
 *  a second, so only the playing track — plus the next one near its end — is kept in memory) */
const PREFETCH = 25;
/** the music volume setting × this = the music bus gain (the mix the SFX were balanced against) */
const MUSIC_LEVEL = 1.0;                // the tracks are −16 LUFS; at 0.6 × the old 0.4 default they sat ~8 dB under the sound effects

interface Track { name: string; source: AudioBufferSourceNode; gain: GainNode; endsAt: number; }

/** A shuffled copy (Fisher–Yates). */
function shuffled<T>(items: T[]): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Sounds: real recordings from `public/sounds/` (see `public/sounds/SOURCES.md`) decoded once and played through
 * WebAudio; a small synthesized cue stands in for any file that is missing. Music: the playlist above through one
 * music bus (the volume setting), a looping horn while ELON is on your tail (you stole his car).
 *
 * Unlock: the context starts on the first real gesture (iOS also needs a sound started inside it — a silent sample);
 * a hidden tab suspends it (music and all — a crossfade waits on the audio clock), a visible one resumes it, and if
 * the browser refuses (iOS after a call) the next tap does. No WebAudio at all = a silent game, never a crash.
 */
export class AudioManager {
  private context?: AudioContext;
  private disabled = false;
  /** a real gesture has happened: until then nothing is queued on the suspended context (no burst at the first tap) */
  private gestured = false;
  /** we suspended the context for a hidden tab and owe it a resume */
  private resumeOnShow = false;
  private buffers = new Map<string, AudioBuffer>();
  private loading = new Map<string, Promise<AudioBuffer | null>>();
  private missing = new Set<string>();
  /** every music track feeds this gain (music volume × MUSIC_LEVEL); each track has its own fade gain before it */
  private musicBus?: GainNode;
  private musicMode?: "menu" | "run";
  /** the run track decoded ahead of time (prefetchRun); trimMusic keeps it */
  private prefetched?: string;
  /** the iOS playback-session switch has been made (unmuteIOS) */
  private unmuted = false;
  /** the track playing (or scheduled to start) now */
  private current?: Track;
  /** tracks fading out: stopped at the end of their fade */
  private fading = new Set<Track>();
  /** the run playlist still to play this round (refilled, reshuffled, when empty) */
  private bag: string[] = [];
  /** the last run track started: never the same one twice in a row */
  private lastRunTrack?: string;
  /** bumps on every music request: a load that finishes for an older request is dropped */
  private musicToken = 0;
  /** the track whose successor is being loaded (one hand-over at a time) */
  private handingOver?: Track;
  private musicWatch?: number;
  private musicTimer?: number;
  private sirenTimer?: number;
  private sirenSource?: AudioBufferSourceNode;
  private step = 0;
  private lastPlayed = new Map<string, number>();
  private paddedCache = new WeakMap<AudioBuffer, AudioBuffer>();

  constructor(private musicVolume = 0.4, private sfxVolume = 0.75) {
    if (typeof window === "undefined") return;
    for (const type of GESTURES) window.addEventListener(type, this.onGesture, { capture: true, passive: true });
    document.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("pageshow", this.onVisibility);
  }

  /** Called from gesture handlers: create/resume the context and prime it with a silent sample (the iOS unlock). */
  unlock(): void {
    const ctx = this.ensureContext();
    if (!ctx || document.hidden) return;
    if (ctx.state !== "running") {
      try { void Promise.resolve(ctx.resume()).catch(() => undefined); } catch { /* refused: the next gesture retries */ }
      try {
        const source = ctx.createBufferSource();
        source.buffer = ctx.createBuffer(1, 1, 22050);
        source.connect(ctx.destination);
        source.start(0);
      } catch { /* nothing to prime */ }
    }
  }

  /** The context, made on first use; undefined (and audio off for good) when the browser has none or refuses one. */
  private ensureContext(): AudioContext | undefined {
    if (this.context || this.disabled) return this.context;
    const Ctor = contextClass();
    if (!Ctor) { this.disabled = true; return undefined; }
    try {
      this.context = new Ctor({ latencyHint: "interactive" });
    } catch {
      try { this.context = new Ctor(); } catch { this.disabled = true; }
    }
    return this.context;
  }

  /**
   * iPhones mute Web Audio with the ring/silent switch (and in-app browsers like X's follow it) unless the page plays as
   * "media": ask WebKit for the playback session (Safari 16.4+); on older WebKit loop a silent <audio> from the first tap,
   * which switches the session the same way. Karolis 2026-10-17: "in the X miniplayer the music doesn't work".
   */
  private unmuteIOS(): void {
    if (this.unmuted) return;
    this.unmuted = true;
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) { try { session.type = "playback"; } catch { /* refused: keep the default */ } return; }
    if (!/iP(hone|ad|od)/.test(navigator.userAgent) && !(/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)) return;
    const el = document.createElement("audio");
    el.setAttribute("playsinline", "");
    el.setAttribute("aria-hidden", "true");
    el.loop = true;
    el.src = SILENT_WAV;
    void el.play().catch(() => { this.unmuted = false; });             // not inside a gesture yet: the next tap tries again
  }

  private onGesture = (): void => {
    this.unmuteIOS();
    this.gestured = true;
    if (this.musicMode === "menu") window.setTimeout(() => this.prefetchRun(), 1500);   // after the lobby song has had its turn to load
    if (this.disabled || (this.context && this.context.state === "running")) return;
    this.unlock();
  };

  private onVisibility = (): void => {
    const ctx = this.context;
    if (!ctx) return;
    if (document.hidden) {
      if (ctx.state === "running") {
        this.resumeOnShow = true;
        try { void Promise.resolve(ctx.suspend()).catch(() => undefined); } catch { /* already gone */ }
      }
    } else if (this.resumeOnShow || (ctx.state as string) === "interrupted") {
      this.resumeOnShow = false;
      try { void Promise.resolve(ctx.resume()).catch(() => undefined); } catch { /* the next gesture retries */ }
    }
  };

  /** Sounds may be scheduled: a context exists, the tab is visible, and the user has touched the page. */
  private get live(): AudioContext | undefined {
    const ctx = this.context;
    if (!ctx || !this.gestured || document.hidden || ctx.state === "closed") return undefined;
    return ctx;
  }

  setVolumes(music: number, sfx: number): void {
    this.musicVolume = music;
    this.sfxVolume = sfx;
    if (this.musicBus) this.musicBus.gain.value = music * MUSIC_LEVEL;
  }

  /**
   * Music for the menu (one calm loop) or a run (the playlist). `fresh` = a new run: the next track of the shuffled
   * playlist even when run music is already playing (results → restart); without it a call for the mode that is
   * already playing is a no-op (a revive keeps its track). A change of track is a crossfade once the new one decodes.
   */
  startMusic(mode: "menu" | "run", fresh = false): void {
    this.unlock();
    if (this.musicMode === mode && !fresh) return;                            // playing or still loading: keep it
    this.musicMode = mode;
    const token = ++this.musicToken;
    this.handingOver = undefined;
    this.stopSynthMusic();                                                   // (only ever running when files are missing)
    const pick = mode === "run" ? this.loadRunTrack() : this.load(MENU_TRACK).then((b) => [MENU_TRACK, b] as const);
    void pick.then(([name, buffer]) => {
      if (token !== this.musicToken || !this.context) return;
      if (!buffer || !name) { this.fadeOutCurrent(); this.startSynthMusic(mode); return; }   // no file at all: the old pulse
      this.stopSynthMusic();
      this.playTrack(name, buffer, this.context.currentTime);
    });
  }

  /** Everything off at once (no fade). */
  stopMusic(): void {
    this.musicToken++;
    this.stopSynthMusic();
    for (const track of [this.current, ...this.fading]) if (track) { try { track.source.stop(); } catch { /* already stopped */ } }
    this.current = undefined;
    this.fading.clear();
    this.handingOver = undefined;
    this.musicMode = undefined;
    if (this.musicWatch) window.clearInterval(this.musicWatch);
    this.musicWatch = undefined;
    this.setSiren(false);
  }

  /** The music bus, made once on the context (the volume setting lives here, never on a fading track's gain). */
  private bus(ctx: AudioContext): GainNode {
    if (!this.musicBus) {
      this.musicBus = ctx.createGain();
      this.musicBus.gain.value = this.musicVolume * MUSIC_LEVEL;
      this.musicBus.connect(ctx.destination);
    }
    return this.musicBus;
  }

  /** Starts `buffer` at context time `at`, crossfading from the track playing now (or straight in when none is). */
  private playTrack(name: string, buffer: AudioBuffer, at: number): void {
    const ctx = this.context;
    if (!ctx) return;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = this.musicMode === "menu" || (this.musicMode === "run" && this.runPool().length < 2);   // one track: it loops
    const gain = ctx.createGain();
    source.connect(gain).connect(this.bus(ctx));
    const fadeIn = !!this.current;
    if (fadeIn) { gain.gain.setValueAtTime(0, at); gain.gain.linearRampToValueAtTime(1, at + CROSSFADE); }
    source.start(at);
    this.fadeOutCurrent(at);
    const track: Track = { name, source, gain, endsAt: source.loop ? Infinity : at + buffer.duration };
    this.current = track;
    if (this.musicMode === "run") this.lastRunTrack = name;
    if (!this.musicWatch) this.musicWatch = window.setInterval(this.watchMusic, 1000);
  }

  /** The playing track fades out over CROSSFADE from `at` (now by default), then stops and its decoded audio is freed. */
  private fadeOutCurrent(at = this.context?.currentTime ?? 0): void {
    const old = this.current;
    this.current = undefined;
    if (!old) return;
    this.fading.add(old);
    const g = old.gain.gain;
    const hold = (g as AudioParam & { cancelAndHoldAtTime?: (t: number) => AudioParam }).cancelAndHoldAtTime;
    if (hold) hold.call(g, at);
    else { g.cancelScheduledValues(at); g.setValueAtTime(g.value, at); }
    g.linearRampToValueAtTime(0, at + CROSSFADE);
    old.source.onended = () => {
      this.fading.delete(old);
      try { old.source.disconnect(); old.gain.disconnect(); } catch { /* gone */ }
      this.trimMusic();
    };
    try { old.source.stop(at + CROSSFADE + 0.05); } catch { /* already stopped */ }
  }

  /** Frees decoded music no longer playing or fading (a 2-minute track is ~45 MB of floats). */
  private trimMusic(): void {
    const keep = new Set([this.current?.name, this.prefetched, ...[...this.fading].map((t) => t.name)]);
    for (const key of [...this.buffers.keys()]) if (key.startsWith("music/") && !keep.has(key)) this.buffers.delete(key);
  }

  /** Once a second while music plays: near the end of a run track, load the next one and schedule the crossfade. */
  private watchMusic = (): void => {
    const ctx = this.context;
    const cur = this.current;
    if (!ctx || !cur || this.musicMode !== "run" || this.handingOver === cur) return;
    if (cur.endsAt - ctx.currentTime > PREFETCH) return;                      // the clock stops while the tab is hidden
    this.handingOver = cur;
    const token = this.musicToken;
    void this.loadRunTrack().then(([name, buffer]) => {
      if (token !== this.musicToken || this.current !== cur) return;
      this.handingOver = undefined;
      if (!buffer || !name || name === cur.name) { cur.source.loop = true; cur.endsAt = Infinity; return; }   // nothing else: loop it
      this.playTrack(name, buffer, Math.max(ctx.currentTime + 0.05, cur.endsAt - CROSSFADE));
    });
  };

  /** The run tracks not known to be missing. */
  private runPool(): string[] {
    return RUN_TRACKS.filter((t) => !this.missing.has(t));
  }

  /** The next track of the shuffled playlist: each one once per round, a new round never opens with the last one. */
  private nextRunTrack(): string | undefined {
    this.fillBag();
    return this.bag.shift();
  }

  /** The shuffle bag, refilled when empty; a new round never opens with the track that just played. */
  private fillBag(): void {
    const pool = this.runPool();
    if (!pool.length) return;
    this.bag = this.bag.filter((t) => !this.missing.has(t) && t !== this.lastRunTrack);
    if (!this.bag.length) {
      this.bag = shuffled(pool);
      if (this.bag.length > 1 && this.bag[0] === this.lastRunTrack) this.bag.push(this.bag.shift() as string);
    }
  }

  /**
   * Fetches and decodes the run track that will play next, while the lobby is up — so a run's music starts the instant
   * the run does (on a phone the fetch + decode of a 2–3 MB track took seconds, and the lobby song played on into the run:
   * Karolis 2026-10-17 "as soon as you start you're locked in"). Kept out of trimMusic() until it plays.
   */
  prefetchRun(): void {
    if (!this.context || this.disabled) return;
    this.fillBag();
    const name = this.bag[0];
    if (!name || this.buffers.has(name) || this.loading.has(name)) return;
    this.prefetched = name;
    void this.load(name);
  }

  /** Loads run tracks from the playlist until one decodes (missing files drop out); [undefined, null] when none exist. */
  private async loadRunTrack(): Promise<readonly [string | undefined, AudioBuffer | null]> {
    for (let tries = 0; tries <= RUN_TRACKS.length; tries++) {
      const name = this.nextRunTrack();
      if (!name) break;
      const buffer = await this.load(name);
      if (buffer) return [name, buffer] as const;
    }
    return [undefined, null] as const;
  }

  /** While ELON is on your tail: his car horn looped (the `siren` cue plays the horn; siren.mp3 stays as a spare), or a two-tone fallback. */
  setSiren(on: boolean): void {
    if (on && !this.sirenTimer && !this.sirenSource) {
      const buffer = this.buffers.get(CHASE_SOUND);
      const ctx = this.context;
      if (buffer && ctx) {                                                     // one looping source: a suspended context just starts it later
        const source = ctx.createBufferSource();
        source.buffer = this.padded(buffer, 1.6);                              // honk-honk, a breath, honk-honk
        source.loop = true;
        const g = ctx.createGain();
        g.gain.value = this.sfxVolume * 0.28;                                  // the horn loop was the loudest thing in a run (−12 LUFS file)
        source.connect(g).connect(ctx.destination);
        source.start();
        this.sirenSource = source;
        return;
      }
      void this.load(CHASE_SOUND);
      let high = false;
      this.sirenTimer = window.setInterval(() => { high = !high; this.tone(high ? 740 : 560, 0.3, "square", this.sfxVolume * 0.06); }, 330);
    } else if (!on) {
      if (this.sirenTimer) window.clearInterval(this.sirenTimer);
      this.sirenTimer = undefined;
      if (this.sirenSource) { try { this.sirenSource.stop(); } catch { /* already stopped */ } }
      this.sirenSource = undefined;
    }
  }

  /** `buffer` followed by silence up to `seconds` (cached), so a short sample loops with a gap. */
  private padded(buffer: AudioBuffer, seconds: number): AudioBuffer {
    const ctx = this.context;
    if (!ctx || buffer.duration >= seconds) return buffer;
    const cached = this.paddedCache.get(buffer);
    if (cached) return cached;
    const out = ctx.createBuffer(buffer.numberOfChannels, Math.ceil(seconds * buffer.sampleRate), buffer.sampleRate);
    for (let c = 0; c < buffer.numberOfChannels; c++) out.getChannelData(c).set(buffer.getChannelData(c));
    this.paddedCache.set(buffer, out);
    return out;
  }

  /** Warms the sample cache so the first hop is not silent. */
  preload(names: string[]): void {
    for (const name of names) void this.load(name);
  }

  cue(name: Cue): void {
    const now = performance.now();
    if (name === "coin" && now - (this.lastPlayed.get(name) ?? 0) < 45) return;    // a coin line is one rattle, not twenty
    this.lastPlayed.set(name, now);
    if (this.playSample(name === "siren" ? CHASE_SOUND : name)) return;
    const presets: Record<Cue, [number, number, OscillatorType]> = {
      ui: [420, 0.045, "sine"], coin: [988, 0.05, "square"], jump: [240, 0.12, "triangle"], duck: [120, 0.1, "sawtooth"], lane: [180, 0.04, "triangle"],
      power: [660, 0.22, "sine"], shield: [330, 0.25, "sawtooth"], nitro: [200, 0.35, "sawtooth"], land: [90, 0.05, "sine"], crash: [70, 0.35, "sawtooth"],
      stumble: [110, 0.2, "sawtooth"], siren: [740, 0.25, "square"], quest: [990, 0.35, "triangle"], revive: [523, 0.4, "triangle"], high: [1320, 0.5, "triangle"],
      board: [520, 0.12, "triangle"], key: [1320, 0.1, "sine"], box: [660, 0.14, "square"], fly: [880, 0.1, "triangle"], bump: [160, 0.08, "sawtooth"], countdown: [660, 0.08, "square"]
    };
    const [frequency, duration, type] = presets[name];
    this.tone(frequency, duration, type, this.sfxVolume * (name === "crash" ? 0.5 : 0.25));
    if (name === "high" || name === "quest") window.setTimeout(() => this.tone(frequency * 1.5, duration * 0.8, type, this.sfxVolume * 0.25), 140);
  }

  private playSample(name: string, gain = 1): boolean {
    const buffer = this.buffers.get(name);
    if (!buffer) {
      if (!this.missing.has(name)) void this.load(name);
      return false;
    }
    const ctx = this.live;
    if (!ctx) return true;                                                     // silent (hidden tab / no gesture yet): no synth stand-in either
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const g = ctx.createGain();
    g.gain.value = this.sfxVolume * gain;
    source.connect(g).connect(ctx.destination);
    source.start();
    return true;
  }

  /** A decoded file from public/sounds/ (cached; one request per file however many callers ask at once). */
  private load(name: string): Promise<AudioBuffer | null> {
    const ready = this.buffers.get(name);
    if (ready) return Promise.resolve(ready);
    if (this.missing.has(name)) return Promise.resolve(null);
    const inFlight = this.loading.get(name);
    if (inFlight) return inFlight;
    const promise = this.fetchDecode(name).finally(() => this.loading.delete(name));
    this.loading.set(name, promise);
    return promise;
  }

  private async fetchDecode(name: string): Promise<AudioBuffer | null> {
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}sounds/${name}.mp3`);
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.arrayBuffer();
      const ctx = this.ensureContext();
      if (!ctx) throw new Error("no WebAudio");
      const buffer = await new Promise<AudioBuffer>((resolve, reject) => {
        const promise = ctx.decodeAudioData(data, resolve, reject) as Promise<AudioBuffer> | undefined;
        promise?.then?.(resolve, reject);
      });
      this.buffers.set(name, buffer);
      return buffer;
    } catch {
      this.missing.add(name);
      return null;
    }
  }

  private startSynthMusic(mode: "menu" | "run"): void {
    const bpm = mode === "run" ? 150 : 100;
    const lead = mode === "run" ? [196, 294, 392, 262, 440, 330, 523, 392] : [196, 262, 294, 392];
    const bass = mode === "run" ? [49, 49, 65, 65, 55, 55, 73, 73] : [49, 49, 65, 65];
    const tick = () => {
      const i = this.step++;
      this.tone(lead[i % lead.length], 0.1, "triangle", this.musicVolume * 0.12);
      if (i % 2 === 0) this.tone(bass[(i / 2) % bass.length], 0.18, "sawtooth", this.musicVolume * 0.1);
    };
    tick();
    this.musicTimer = window.setInterval(tick, 60_000 / bpm / 2);
  }

  private stopSynthMusic(): void {
    if (this.musicTimer) window.clearInterval(this.musicTimer);
    this.musicTimer = undefined;
  }

  private tone(frequency: number, duration: number, type: OscillatorType, volume: number): void {
    const ctx = this.live;
    if (!ctx || volume <= 0) return;                                           // a hidden tab queues nothing (no chord burst on return)
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + duration);
  }
}
